// RecentMessage.tsx
import React from "react";
import { Mail, MessageSquare } from "lucide-react";
import { Card } from "@shared/ui/card";

interface RecentMessageItem {
  id: number;
  statusDisplay: string;
  channelType: string;
  message: {
    to: string | string[];
    body?: string;
    subject?: string;
  };
  metadata: {
    failedAt?: string;
    channel: number;
  };
}

interface RecentMessageProps {
  messages?: RecentMessageItem[];
}

const RecentMessage: React.FC<RecentMessageProps> = ({ messages = [] }) => {
  const getChannelDisplay = (channelType: string) => {
    switch (channelType) {
      case "0":
        return "Email";
      case "1":
        return "SMS";
      case "2":
        return "WhatsApp";
      default:
        return "Notification";
    }
  };

  const getChannelIcon = (channelType: string) => {
    switch (channelType) {
      case "0":
        return <Mail className="w-5 h-5 text-blue-600" />;
      case "1":
        return <MessageSquare className="w-5 h-5 text-blue-600" />;
      default:
        return <Mail className="w-5 h-5 text-blue-600" />;
    }
  };

  const getRecipient = (message: RecentMessageItem["message"]) => {
    if (Array.isArray(message.to)) {
      return message.to[0] || "N/A";
    }
    return message.to || "N/A";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "sent":
      case "delivered":
        return "bg-green-600";
      case "failed":
        return "bg-red-600";
      case "pending":
        return "bg-yellow-600";
      default:
        return "bg-blue-600";
    }
  };

  if (!messages?.length) {
    return (
      <Card className="p-6">
        <h2 className="text-sm font-bold text-gray-900 mb-4">
          Recent Messages (Last 10)
        </h2>
        <div className="text-gray-500 text-sm py-4">
          No recent messages found
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-sm font-bold text-gray-900 mb-4">
        Recent Messages (Last 10)
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {messages.slice(0, 10).map((message) => (
          <div
            key={message?.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer bg-white"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    {getChannelIcon(message?.channelType)}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {getRecipient(message?.message)}
                  </h3>

                  <div className="text-xs text-gray-600 mb-2">
                    <span>{getChannelDisplay(message?.channelType)}</span>
                    {message?.message?.subject && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="truncate">
                          {message?.message?.subject}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <span
                  className={`px-2 py-1 text-xs font-medium text-white rounded ${getStatusColor(
                    message?.statusDisplay,
                  )}`}
                >
                  {message?.statusDisplay}
                </span>
                <span className="text-xs text-gray-500">
                  {message?.metadata?.failedAt || "N/A"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default RecentMessage;
